import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatchSuggestion } from '../../../models/bill-import.models';
import { MIN_DISPLAY_SCORE, getScoreClass, ScoreClass } from '../../../utils/match-score.util';
import { BillAiFlowStore } from '../state/bill-ai-flow.store';
import { MatRadioModule } from '@angular/material/radio';
import { ScoreSelectComponent, ScoreSelectOption } from '../score-select/score-select.component';

interface MatchOption {
  value: number | string;
  label: string;
  scorePercent: number;
  scoreClass: ScoreClass;
}

@Component({
  selector: 'app-entity-match-selector',
  standalone: true,
  imports: [CommonModule, MatRadioModule, ScoreSelectComponent],
  templateUrl: './entity-match-selector.component.html',
  styleUrl: './entity-match-selector.component.scss'
})
export class EntityMatchSelectorComponent implements OnInit, OnChanges {
  @Input() groupId!: string;
  @Input() match: MatchSuggestion | null = null;
  @Input() alternatives: MatchSuggestion[] = [];
  @Input() extractedName = '';
  @Input() forcedNew = false;
  @Input() forcedNewReason = 'Parent is new';
  @Input() showDropdown = true;
  /** Duplicate exists under a different entity — both tabs disabled. */
  @Input() blocked = false;
  @Input() blockedReason = 'Exists under a different entity — migrate the import or edit the entry';
  /** Exact in-scope duplicate — linked to the existing record, Create New disabled. */
  private _exactLocked = false;
  @Input() set exactLocked(value: boolean) {
    this._exactLocked = value;
    // Locked rows are linked by definition — never render with no tab checked.
    if (value) {
      this.isCreateNew = false;
    }
  }
  get exactLocked(): boolean {
    return this._exactLocked;
  }
  @Input() exactLockedReason = 'Already exists — linked to the existing record';
  @Output() selectionChange = new EventEmitter<number | string | null>();

  isCreateNew = true;
  hasMatches = false;
  matchOptions: MatchOption[] = [];
  scoreSelectOptions: ScoreSelectOption[] = [];
  currentSelection: number | string | null = null;
  selectedOption: MatchOption | null = null;
  bestScorePercent = 0;
  bestScoreClass = '';

  private state = inject(BillAiFlowStore);

  ngOnInit(): void {
    this.computeState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.computeState();
  }

  private computeState(): void {
    // Build match options from match + alternatives, filtering out low-confidence matches
    this.matchOptions = [];

    if (this.match && this.match.score >= MIN_DISPLAY_SCORE) {
      const pct = Math.round(this.match.score * 100);
      this.matchOptions.push({
        value: this.match.id,
        label: this.match.label,
        scorePercent: pct,
        scoreClass: getScoreClass(pct)
      });
    }

    for (const alt of this.alternatives || []) {
      if (alt.score < MIN_DISPLAY_SCORE) continue;
      if (!this.match || alt.id !== this.match.id) {
        const pct = Math.round(alt.score * 100);
        this.matchOptions.push({
          value: alt.id,
          label: alt.label,
          scorePercent: pct,
          scoreClass: getScoreClass(pct)
        });
      }
    }

    this.hasMatches = this.matchOptions.length > 0;
    this.bestScorePercent = this.matchOptions.length > 0 ? this.matchOptions[0].scorePercent : 0;
    this.bestScoreClass = getScoreClass(this.bestScorePercent);

    // Build score-select options from matchOptions
    this.scoreSelectOptions = this.matchOptions.map(o => ({
      value: o.value,
      label: o.label,
      score: o.scorePercent,
      scoreClass: o.scoreClass
    }));

    // Read current selection from state ('new_record' is the legacy create-new value)
    const selection = this.state.getSelection(this.groupId);
    this.currentSelection = selection === 'new_record' ? null : selection;
    this.isCreateNew = this.currentSelection === null && !this._exactLocked;
    this.selectedOption = this.matchOptions.find(o => o.value === this.currentSelection) ?? null;
  }

  selectCreateNew(): void {
    if (this.blocked || this.exactLocked) return;
    this.isCreateNew = true;
    this.currentSelection = null;
    this.selectionChange.emit(null);
  }

  selectMatch(): void {
    if (!this.hasMatches || this.blocked) return;
    this.isCreateNew = false;
    const best = this.matchOptions[0];
    this.currentSelection = best.value;
    this.selectedOption = best;
    this.selectionChange.emit(best.value);
  }

  onMatchSelected(value: number | string): void {
    this.currentSelection = value;
    this.selectedOption = this.matchOptions.find(o => o.value === value) ?? null;
    this.selectionChange.emit(value);
  }

}
